/**
 * Coordinates the Vault browser's query, selection, and mutation workflows.
 *
 * VaultView composes catalog, stored items, live workspace projections, and jobs.
 * Keeping orchestration
 * here gives the page one coherent state machine while API services preserve
 * subsystem and authorization boundaries.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useServices } from '@/app-shell/services'
import { Button, Card, CardContent } from '@/components/ui'
import {
  createFolder,
  createNote,
  copyItem,
  downloadItem,
  listChildren,
  listSpecial,
  restoreItem,
  saveNote,
  searchItems,
  trashItem,
  updateItem,
} from '../services/vault-api'
import type {
  FolderContentsResponse,
  ItemListResponse,
  ItemResponse,
  VaultLayout,
  VaultRecord,
} from '../types'
import type { VaultDragPayload } from '../lib/format'
import { NewItemDialog, RenameItemDialog } from './NewItemDialogs'
import { ShareDialog } from './ShareDialog'
import { TransferBar } from './TransferBar'
import { VaultBreadcrumbs } from './VaultBreadcrumbs'
import { VaultBrowser } from './VaultBrowser'
import { VaultNoteEditor } from './VaultNoteEditor'
import { VaultToolbar, type VaultTab } from './VaultToolbar'
import { VaultVersionHistoryDialog } from './VaultVersionHistoryDialog'
import { VaultInspector } from './VaultInspector'
import { useVaultUploads } from '../hooks/useVaultUploads'
import { useVaultDownloads } from '../hooks/useVaultDownloads'
import { VaultMoveConfirmation } from './VaultMoveConfirmation'
import { waitForVaultJob } from '../services/vault-jobs'
import { triggerBrowserDownload } from '../services/browser-download'
import { VaultHelpDialog } from './VaultHelpDialog'
import {
  VaultConnectorSummary,
  type VaultConnectorSummaryProps,
} from './VaultConnectorSummary'

type Transfer = { kind: 'move' | 'copy'; entry: ItemResponse; name: string }
type PendingMove = { payload: VaultDragPayload; targetId: string }

export function VaultView({
  orgId,
  vault,
  initialTab = 'files',
  connectorSummary,
}: {
  orgId: string
  vault: VaultRecord
  initialTab?: VaultTab
  connectorSummary?: VaultConnectorSummaryProps
}) {
  const vaultId = vault.vaultId
  const api = useServices().getClient()
  const queryClient = useQueryClient()
  const [folderId, setFolderId] = useState('ROOT')
  const [tab, setTab] = useState<VaultTab>(initialTab)
  const [search, setSearch] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const [newKind, setNewKind] = useState<'folder' | 'note' | null>(null)
  const [note, setNote] = useState<ItemResponse | null>(null)
  const [rename, setRename] = useState<ItemResponse | null>(null)
  const [share, setShare] = useState<ItemResponse | null>(null)
  const [details, setDetails] = useState<ItemResponse | null>(null)
  const [layout, setLayout] = useState<VaultLayout>('list')
  const [versionHistory, setVersionHistory] = useState<ItemResponse | null>(null)
  const [transfer, setTransfer] = useState<Transfer | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    setFolderId('ROOT')
    setTab(initialTab)
    setSearch('')
    setSubmittedSearch('')
  }, [initialTab, vaultId])

  // Live search after a short pause instead of an explicit submit action
  useEffect(() => {
    const timer = window.setTimeout(() => setSubmittedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const view = tab !== 'files' ? tab : submittedSearch ? 'search' : 'folder'
  const listKey = ['vault-items', orgId, vaultId, view, folderId, submittedSearch]
  const listQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) => {
      if (view === 'notes' || view === 'shared-with-me' || view === 'trash') {
        return listSpecial(api, orgId, vaultId, view)
      }
      if (view === 'search') return searchItems(api, orgId, vaultId, submittedSearch)
      return listChildren(api, orgId, vaultId, folderId, pageParam || undefined)
    },
    initialPageParam: '',
    getNextPageParam: (page) => page.nextToken || undefined,
    // A previous folder page can reveal names the user cannot see in the next folder.
    placeholderData: undefined,
  })
  const items = listQuery.data?.pages.flatMap((page) => page.items) ?? []
  const listError = listQuery.error as (Error & { response?: { status?: number } }) | null
  const accessDenied = listError?.response?.status === 403
  const firstPage = listQuery.data?.pages[0]
  const folder = firstPage && isFolderContents(firstPage)
    ? firstPage.folder
    : undefined
  const downloads = useVaultDownloads({
    api,
    orgId,
    vaultId,
    items,
    scope: `${view}:${folderId}:${submittedSearch}`,
  })
  const canEditFolder = folder?.effectiveRole === 'editor'
    || folder?.effectiveRole === 'owner'
  const folderReadOnly = folder?.item.readOnly ?? false
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['vault-items', orgId, vaultId] })
  }, [orgId, queryClient, vaultId])

  const refreshWithFeedback = useCallback(async () => {
    setRefreshing(true)
    try {
      await refresh()
      toast.success('Vault refreshed')
    } catch {
      toast.error('Vault refresh failed')
    } finally {
      setRefreshing(false)
    }
  }, [refresh])

  const run = useCallback(async (action: () => Promise<unknown>, success: string) => {
    try {
      await action()
      await refresh()
      toast.success(success)
      return true
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Vault action failed')
      return false
    }
  }, [refresh])
  const uploads = useVaultUploads({
    api,
    orgId,
    vaultId,
    folderId,
    refresh,
    run,
    closeVersionHistory: () => setVersionHistory(null),
  })

  const breadcrumbs = useMemo(
    () => [...(folder?.breadcrumbs ?? []), ...(folder ? [folder.item] : [])],
    [folder],
  )

  const openFolder = (id: string) => {
    setDetails(null)
    setFolderId(id)
    setTab('files')
    setSearch('')
    setSubmittedSearch('')
  }

  const open = (entry: ItemResponse) => {
    if (entry.item.kind === 'folder') openFolder(entry.item.itemId)
    else if (entry.item.kind === 'note') setNote(entry)
    else download(entry)
  }

  const download = (entry: ItemResponse) =>
    void run(async () => triggerBrowserDownload(await downloadItem(api, orgId, vaultId, entry.item.itemId)), 'Download ready')

  const moveItem = useCallback(async (payload: VaultDragPayload, targetId: string, confirmed = false) => {
    try {
      await updateItem(api, orgId, vaultId, payload.itemId, payload.revision, {
        parentId: targetId,
        confirmPermissionChange: confirmed,
      })
      await refresh()
      toast.success(`Moved “${payload.name}”`)
    } catch (error: any) {
      const detail: string = error?.response?.data?.detail ?? ''
      if (error?.response?.status === 409 && detail.includes('confirmation required')) {
        setPendingMove({ payload, targetId })
      } else {
        toast.error(detail || 'Move failed')
      }
    }
  }, [api, orgId, refresh, vaultId])

  const startTransfer = (kind: Transfer['kind'], entry: ItemResponse) => {
    setTransfer({ kind, entry, name: kind === 'copy' ? `${entry.item.name} copy` : entry.item.name })
    openFolder('ROOT')
  }

  const finishTransfer = async () => {
    if (!transfer) return
    const success = await run(
      () => transfer.kind === 'move'
        ? updateItem(api, orgId, vaultId, transfer.entry.item.itemId, transfer.entry.item.revision, {
            parentId: folderId,
            confirmPermissionChange: true,
          })
        : (async () => {
            const response = await copyItem(
              api,
              orgId,
              vaultId,
              transfer.entry.item.itemId,
              folderId,
              transfer.name.trim(),
            )
            if (response.jobId) {
              await waitForVaultJob(api, orgId, vaultId, response.jobId)
            }
          })(),
      transfer.kind === 'move' ? 'Item moved' : 'Item copied',
    )
    if (success) setTransfer(null)
  }

  return (
    <div className="min-h-[70vh] space-y-4 p-4 sm:p-6">
      {uploads.inputs}

      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1 space-y-4">
      <VaultToolbar
        title={vault.name}
        description={vault.description || 'Secure files, folders, and notes.'}
        search={search}
        tab={tab}
        showFileActions={view === 'folder' && canEditFolder && !folderReadOnly}
        showNoteAction={view === 'notes'}
        layout={layout}
        selectedCount={downloads.selectedIds.size}
        preparingDownload={downloads.preparing}
        refreshing={refreshing}
        onSearchChange={setSearch}
        onTabChange={(value) => {
          setTab(value)
          setSearch('')
          setSubmittedSearch('')
          setDetails(null)
        }}
        onUpload={() => uploads.fileInput.current?.click()}
        onNewFolder={() => setNewKind('folder')}
        onNewNote={() => setNewKind('note')}
        onShowHelp={() => setHelpOpen(true)}
        onLayoutChange={setLayout}
        onDownloadSelection={() => void downloads.download()}
        onRefresh={() => void refreshWithFeedback()}
      />

      {connectorSummary && <VaultConnectorSummary {...connectorSummary} />}

      {view === 'folder' && (
        <VaultBreadcrumbs
          items={breadcrumbs}
          onNavigate={openFolder}
          onDropItem={(payload, targetId) => void moveItem(payload, targetId)}
        />
      )}

      {transfer && (
        <TransferBar
          kind={transfer.kind}
          entry={transfer.entry}
          name={transfer.name}
          canConfirm={view === 'folder' && canEditFolder && !folderReadOnly}
          onNameChange={(name) => setTransfer({ ...transfer, name })}
          onCancel={() => setTransfer(null)}
          onConfirm={() => void finishTransfer()}
        />
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <VaultBrowser
            items={items}
            loading={listQuery.isPending}
            error={listQuery.isError && !accessDenied}
            accessDenied={accessDenied}
            view={view}
            canUpload={canEditFolder && !folderReadOnly}
            canCreateFolder={canEditFolder && !folderReadOnly}
            folderId={folderId}
            layout={layout}
            selectedItemId={details?.item.itemId}
            selectedIds={downloads.selectedIds}
            onOpen={open}
            onDownload={download}
            onShare={setShare}
            onRename={setRename}
            onMove={(entry) => startTransfer('move', entry)}
            onCopy={(entry) => startTransfer('copy', entry)}
            onNewVersion={uploads.chooseVersion}
            onVersionHistory={setVersionHistory}
            onDetails={setDetails}
            onTrash={(entry) => {
              if (details?.item.itemId === entry.item.itemId) setDetails(null)
              void run(() => trashItem(api, orgId, vaultId, entry.item.itemId), 'Moved to trash')
            }}
            onRestore={(entry) => void run(() => restoreItem(api, orgId, vaultId, entry.item.itemId), 'Item restored')}
            onDropItem={(payload, targetId) => void moveItem(payload, targetId)}
            onDropFiles={uploads.uploadFiles}
            onUploadClick={() => uploads.fileInput.current?.click()}
            onNewFolderClick={() => setNewKind('folder')}
            onRetry={() => void listQuery.refetch()}
            onSelectionChange={downloads.setSelected}
            onSelectAll={downloads.selectAll}
          />
          {listQuery.hasNextPage && (
            <div className="border-t p-3 text-center">
              <Button
                variant="outline"
                size="sm"
                disabled={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                {listQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
        </div>
        <VaultInspector
          api={api}
          orgId={orgId}
          vaultId={vaultId}
          entry={details}
          onClose={() => setDetails(null)}
          onShare={setShare}
        />
      </div>

      <NewItemDialog
        kind={newKind ?? 'folder'}
        open={newKind !== null}
        onOpenChange={(open) => { if (!open) setNewKind(null) }}
        onCreate={(name) => run(
          () => newKind === 'note'
            ? createNote(api, orgId, vaultId, name)
            : createFolder(api, orgId, vaultId, folderId, name),
          `${newKind === 'note' ? 'Note' : 'Folder'} created`,
        )}
      />
      <RenameItemDialog
        name={rename?.item.name ?? null}
        onOpenChange={(open) => { if (!open) setRename(null) }}
        onRename={(name) => run(
          () => updateItem(api, orgId, vaultId, rename!.item.itemId, rename!.item.revision, { name }),
          'Item renamed',
        )}
      />
      <VaultNoteEditor
        note={note}
        onClose={() => setNote(null)}
        onSave={(body, revision) => saveNote(api, orgId, vaultId, note!.item.itemId, body, revision)}
      />
      <VaultVersionHistoryDialog
        api={api}
        orgId={orgId}
        vaultId={vaultId}
        entry={versionHistory}
        onClose={() => setVersionHistory(null)}
        onUploadVersion={uploads.chooseVersion}
      />
      <ShareDialog
        api={api}
        orgId={orgId}
        vaultId={vaultId}
        entry={share}
        onClose={() => setShare(null)}
      />
      <VaultHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      <VaultMoveConfirmation
        move={pendingMove}
        onCancel={() => setPendingMove(null)}
        onConfirm={(payload, targetId) => {
          setPendingMove(null)
          void moveItem(payload, targetId, true)
        }}
      />
    </div>
  )
}

function isFolderContents(
  page: ItemListResponse,
): page is FolderContentsResponse {
  return 'folder' in page
}
